import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import API_BASE from '../config';
import '../styles/Admin.css';

const STATUS_LABELS = { IN_PROCESS: 'In Process', HIRED: 'Hired', REJECTED: 'Rejected', ON_HOLD: 'On Hold' };
const STATUS_COLORS = { IN_PROCESS: '#3498db', HIRED: '#27ae60', REJECTED: '#e74c3c', ON_HOLD: '#f39c12' };

function RecruitmentOnboarding() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // Candidates list
  const [candidates, setCandidates]  = useState([]);
  const [total, setTotal]            = useState(0);
  const [search, setSearch]          = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]        = useState(true);

  // Form
  const [showForm, setShowForm]      = useState(false);
  const [editing, setEditing]        = useState(null); // candidate being edited
  const [form, setForm]              = useState({ fullName: '', address: '', telephone: '', email: '', salaryExpectation: '', campaign: '', startDate: '' });

  // Notes
  const [noteText, setNoteText]      = useState('');
  const [expandedId, setExpandedId]  = useState(null);

  // Clients for campaign dropdown
  const [clientList, setClientList]  = useState([]);

  // Bulk selection for test emails
  const [selectedIds, setSelectedIds]      = useState(new Set());
  const [testLanguage, setTestLanguage]    = useState('English');
  const [sending, setSending]              = useState(false);
  const [sendResult, setSendResult]        = useState(null);

  // Import test modal
  const [showImport, setShowImport]            = useState(false);
  const [importData, setImportData]            = useState(null);
  const [importFileName, setImportFileName]    = useState('');
  const [importing, setImporting]              = useState(false);
  const [importTab, setImportTab]              = useState('json');
  const fileInputRef = useRef(null);

  // ── Fetch candidates ──────────────────────────────────────────────────────
  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (search)       qs.set('search', search);
      if (statusFilter) qs.set('status', statusFilter);
      const res = await fetch(`${API_BASE}/recruitment/candidates?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setCandidates(data.candidates || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, search, statusFilter]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  // Fetch clients for campaign dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/clients`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setClientList(data.clients || []);
        }
      } catch (err) {
        console.error('Fetch clients error:', err);
      }
    })();
  }, [token]);

  // ── Create / Update ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) return alert('Full Name is required');

    const url = editing
      ? `${API_BASE}/recruitment/candidates/${editing.id}`
      : `${API_BASE}/recruitment/candidates`;
    const method = editing ? 'PATCH' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.message || 'Failed'); }
      resetForm();
      fetchCandidates();
    } catch (err) {
      alert(err.message);
    }
  };

  // ── Status change ─────────────────────────────────────────────────────────
  const changeStatus = async (id, newStatus) => {
    try {
      await fetch(`${API_BASE}/recruitment/candidates/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchCandidates();
    } catch (err) {
      console.error('Status change error:', err);
    }
  };

  // ── Add note ──────────────────────────────────────────────────────────────
  const addNote = async (candidateId) => {
    if (!noteText.trim()) return;
    try {
      await fetch(`${API_BASE}/recruitment/candidates/${candidateId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: noteText }),
      });
      setNoteText('');
      fetchCandidates();
    } catch (err) {
      console.error('Add note error:', err);
    }
  };

  // ── Delete candidate ──────────────────────────────────────────────────────
  const deleteCandidate = async (id) => {
    if (!window.confirm('Delete this candidate permanently?')) return;
    try {
      await fetch(`${API_BASE}/recruitment/candidates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCandidates();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // ── Edit prefill ──────────────────────────────────────────────────────────
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      fullName:          c.fullName || '',
      address:           c.address || '',
      telephone:         c.telephone || '',
      email:             c.email || '',
      salaryExpectation: c.salaryExpectation || '',
      campaign:          c.campaign || '',
      startDate:         c.startDate ? c.startDate.slice(0, 10) : '',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ fullName: '', address: '', telephone: '', email: '', salaryExpectation: '', campaign: '', startDate: '' });
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  // ── Bulk selection helpers ────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === candidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(candidates.map((c) => c.id)));
    }
  };

  // ── Send test email ──────────────────────────────────────────────────────
  const handleSendTest = async () => {
    if (selectedIds.size === 0) return alert('Select at least one candidate');
    if (!testLanguage) return alert('Select a language');
    if (!window.confirm(`Send ${testLanguage} test to ${selectedIds.size} candidate(s)?`)) return;

    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API_BASE}/recruitment/send-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidateIds: [...selectedIds], language: testLanguage }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      setSendResult(data);
      setSelectedIds(new Set());
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  // ── Import test ──────────────────────────────────────────────────────────
  const parseCSVTest = (text) => {
    const lines = text.trim().split('\n').map(l => l.split(',').map(c => c.trim().replace(/^"|"$/g, '')));
    if (lines.length < 2) throw new Error('CSV must have a header row and at least one question');
    const questions = lines.slice(1).filter(r => r.length >= 2 && r[1]).map(row => {
      const type = (row[0] || '').toLowerCase() === 'open' ? 'open' : 'closed';
      const q = { type, question: row[1] };
      if (type === 'closed') {
        const opts = [row[2], row[3], row[4], row[5]].filter(Boolean);
        const letters = ['A', 'B', 'C', 'D'];
        q.options = opts.map((o, i) => `${letters[i]}) ${o}`);
        q.answer = row[6] || 'A';
      }
      return q;
    });
    return { language: 'Custom', title: 'Imported CSV Test', questions };
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        if (file.name.endsWith('.csv')) {
          setImportData(parseCSVTest(text));
        } else {
          setImportData(JSON.parse(text));
        }
      } catch (err) {
        alert('Invalid file: ' + err.message);
        setImportData(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImportTest = async () => {
    if (!importData) return alert('No valid test data loaded');
    setImporting(true);
    try {
      const payload = {
        language: importData.language || importData.title || 'Custom',
        title: importData.title || undefined,
        instructions: importData.instructions || undefined,
        questions: importData.questions || [],
      };
      const res = await fetch(`${API_BASE}/recruitment/import-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      alert(data.message);
      setShowImport(false);
      setImportData(null);
      setImportFileName('');
    } catch (err) {
      alert(err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="admin-container">
      {/* Header */}
      <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Recruitment — Onboarding</h1>
          <p style={{ color: '#888', fontSize: '14px' }}>Manage recruitment candidates and interview notes</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/admin')} className="btn-back-nav">← Back</button>
          <button onClick={() => { setShowImport(true); }} style={btnStyle('#8e44ad')}>📥 Import Test</button>
          <button onClick={() => { resetForm(); setShowForm(!showForm); }} style={btnStyle('#27ae60')}>
            {showForm ? '✕ Close Form' : '＋ New Candidate'}
          </button>
        </div>
      </div>

      {/* New / Edit form */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '28px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: editing ? '2px solid #f39c12' : '2px solid #27ae60' }}>
          <h2 style={{ marginTop: 0, color: editing ? '#f39c12' : '#27ae60' }}>
            {editing ? `✏️ Edit — ${editing.fullName}` : '📝 Register New Candidate'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Field label="Full Name *" value={form.fullName} onChange={(v) => setForm({ ...form, fullName: v })} required />
              <Field label="E-Mail Address" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
              <Field label="Telephone Number" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} />
              <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
              <Field label="Salary Expectation (Gross per Month)" value={form.salaryExpectation} onChange={(v) => setForm({ ...form, salaryExpectation: v })} />
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#555' }}>
                Campaign
                <select
                  value={form.campaign}
                  onChange={(e) => setForm({ ...form, campaign: e.target.value })}
                  style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
                >
                  <option value="">— Select Client / Campaign —</option>
                  {clientList.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </label>
              <Field label="Start Date" value={form.startDate} onChange={(v) => setForm({ ...form, startDate: v })} type="date" />
            </div>
            <div style={{ marginTop: '18px', display: 'flex', gap: '10px' }}>
              <button type="submit" style={btnStyle(editing ? '#f39c12' : '#27ae60')}>{editing ? 'Save Changes' : 'Create Candidate'}</button>
              <button type="button" onClick={resetForm} style={btnStyle('#6c757d')}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, email, campaign, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '220px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', minWidth: '140px' }}
        >
          <option value="">All Statuses</option>
          <option value="IN_PROCESS">In Process</option>
          <option value="HIRED">Hired</option>
          <option value="REJECTED">Rejected</option>
          <option value="ON_HOLD">On Hold</option>
        </select>
        <span style={{ padding: '10px', color: '#888', fontSize: '13px' }}>
          {total} candidate{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Bulk action bar */}
      {candidates.length > 0 && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap', background: selectedIds.size > 0 ? '#eef2ff' : '#f8f9fa', padding: '12px 16px', borderRadius: '8px', border: selectedIds.size > 0 ? '1px solid #667eea40' : '1px solid #eee' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={candidates.length > 0 && selectedIds.size === candidates.length}
              onChange={toggleSelectAll}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select All'}
          </label>
          {selectedIds.size > 0 && (
            <>
              <div style={{ width: '1px', height: '28px', background: '#ccc' }} />
              <select
                value={testLanguage}
                onChange={(e) => setTestLanguage(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
              >
                <option value="English">English</option>
                <option value="German">German</option>
                <option value="Spanish">Spanish</option>
              </select>
              <button
                onClick={handleSendTest}
                disabled={sending}
                style={{ ...btnStyle('#667eea'), opacity: sending ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                {sending ? '⏳ Sending...' : '📧 Send Test'}
              </button>
            </>
          )}
          {sendResult && (
            <span style={{ fontSize: '13px', color: sendResult.sent > 0 ? '#27ae60' : '#e74c3c', fontWeight: 600, marginLeft: 'auto' }}>
              ✓ {sendResult.message}
            </span>
          )}
        </div>
      )}

      {/* Candidates list */}
      {loading ? (
        <p style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</p>
      ) : candidates.length === 0 ? (
        <p style={{ textAlign: 'center', padding: '40px', color: '#888' }}>No candidates found. Click "New Candidate" to start.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {candidates.map((c) => (
            <div key={c.id} style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${STATUS_COLORS[c.status] || '#ddd'}40`, overflow: 'hidden' }}>
              {/* Candidate row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', flexWrap: 'wrap', gap: '10px', cursor: 'pointer' }}
                   onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(c.id)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                  />
                  <span style={{ fontWeight: 700, fontSize: '15px', color: '#222' }}>{c.fullName}</span>
                  <span style={{ background: STATUS_COLORS[c.status], color: '#fff', padding: '3px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: 600 }}>
                    {STATUS_LABELS[c.status]}
                  </span>
                  {c.campaign && <span style={{ color: '#888', fontSize: '13px' }}>📋 {c.campaign}</span>}
                  {c.startDate && <span style={{ color: '#888', fontSize: '13px' }}>📅 {fmtDate(c.startDate)}</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button onClick={(e) => { e.stopPropagation(); startEdit(c); }} style={smallBtn('#3498db')}>✏️ Edit</button>
                  <select
                    value={c.status}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => changeStatus(c.id, e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' }}
                  >
                    {Object.keys(STATUS_LABELS).map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                  {(user?.role === 'SUPERADMIN' || user?.role === 'COUNTRY_ADMIN') && (
                    <button onClick={(e) => { e.stopPropagation(); deleteCandidate(c.id); }} style={smallBtn('#e74c3c')}>🗑️</button>
                  )}
                </div>
              </div>

              {/* Expanded details + notes */}
              {expandedId === c.id && (
                <div style={{ padding: '0 22px 20px 22px', borderTop: '1px solid #eee' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', padding: '16px 0', fontSize: '13px' }}>
                    <Detail label="Email" value={c.email} />
                    <Detail label="Telephone" value={c.telephone} />
                    <Detail label="Address" value={c.address} />
                    <Detail label="Salary Expectation (Gross/Month)" value={c.salaryExpectation} />
                    <Detail label="Campaign" value={c.campaign} />
                    <Detail label="Start Date" value={fmtDate(c.startDate)} />
                    <Detail label="Created" value={`${fmtDate(c.createdAt)} by ${c.createdBy ? `${c.createdBy.firstName} ${c.createdBy.lastName}` : '—'}`} />
                    {c.country && <Detail label="Country" value={c.country.name} />}
                  </div>

                  {/* Notes timeline */}
                  <div style={{ marginTop: '8px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#555' }}>📒 Interview Notes</h4>
                    {c.notes && c.notes.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
                        {c.notes.map((n) => (
                          <div key={n.id} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px 14px', borderLeft: '3px solid #667eea' }}>
                            <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                              {fmtTime(n.createdAt)} — <strong>{n.createdBy ? `${n.createdBy.firstName} ${n.createdBy.lastName}` : 'Unknown'}</strong>
                            </div>
                            <div style={{ fontSize: '13px', color: '#333', whiteSpace: 'pre-wrap' }}>{n.note}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#aaa', fontSize: '13px' }}>No notes yet.</p>
                    )}

                    {/* Add note */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <input
                        type="text"
                        placeholder="Add interview note..."
                        value={expandedId === c.id ? noteText : ''}
                        onChange={(e) => setNoteText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addNote(c.id)}
                        style={{ flex: 1, padding: '9px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}
                      />
                      <button onClick={() => addNote(c.id)} style={btnStyle('#667eea')}>Add Note</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Import Test Modal */}
      {showImport && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setShowImport(false)}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', maxWidth: '560px', width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
               onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px 0', color: '#8e44ad' }}>📥 Import Language Test</h2>
            <p style={{ color: '#888', fontSize: '13px', margin: '0 0 20px 0' }}>
              Upload a <strong>JSON</strong> or <strong>CSV</strong> file containing test questions.
            </p>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <button onClick={() => setImportTab('json')} style={{ ...btnStyle(importTab === 'json' ? '#667eea' : '#e0e0e0'), color: importTab === 'json' ? '#fff' : '#555', flex: 1 }}>JSON</button>
              <button onClick={() => setImportTab('csv')} style={{ ...btnStyle(importTab === 'csv' ? '#667eea' : '#e0e0e0'), color: importTab === 'csv' ? '#fff' : '#555', flex: 1 }}>CSV</button>
            </div>

            {importTab === 'json' ? (
              <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #eee' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#555' }}>JSON format:</p>
                <pre style={{ margin: 0, fontSize: '11px', color: '#666', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{`{
  "language": "French",
  "title": "French Language Test",
  "instructions": "Answer all questions...",
  "questions": [
    { "type": "closed", "question": "...", "options": ["A) ...", "B) ..."], "answer": "A" },
    { "type": "open", "question": "Write about..." }
  ]
}`}</pre>
              </div>
            ) : (
              <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #eee' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#555' }}>CSV format (comma-separated):</p>
                <pre style={{ margin: 0, fontSize: '11px', color: '#666', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{`type,question,optionA,optionB,optionC,optionD,answer
closed,"Which is correct?",Option A,Option B,Option C,Option D,A
open,"Describe your experience",,,,, `}</pre>
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#888' }}>First row = header. Leave option columns empty for open questions.</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              style={{ ...btnStyle('#8e44ad'), width: '100%', textAlign: 'center', marginBottom: '12px' }}
            >
              {importFileName ? `📄 ${importFileName}` : '📂 Choose JSON file...'}
            </button>

            {importData && (
              <div style={{ background: '#eef2ff', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #667eea40' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '14px', color: '#333' }}>
                  {importData.title || importData.language || 'Unnamed Test'}
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  {importData.questions?.length || 0} questions • Language: {importData.language || 'N/A'}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowImport(false); setImportData(null); setImportFileName(''); }} style={btnStyle('#6c757d')}>Cancel</button>
              <button
                onClick={handleImportTest}
                disabled={!importData || importing}
                style={{ ...btnStyle('#27ae60'), opacity: (!importData || importing) ? 0.5 : 1 }}
              >
                {importing ? 'Importing...' : 'Import Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tiny sub-components ───────────────────────────────────────────────────────
const Field = ({ label, value, onChange, type = 'text', required = false }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#555' }}>
    {label}
    <input
      type={type}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
    />
  </label>
);

const Detail = ({ label, value }) => (
  <div>
    <span style={{ color: '#999', fontSize: '12px' }}>{label}:</span><br />
    <span style={{ color: '#333', fontWeight: 500 }}>{value || '—'}</span>
  </div>
);

const btnStyle = (bg) => ({
  background: bg, color: '#fff', border: 'none', padding: '9px 18px',
  borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
});

const smallBtn = (bg) => ({
  background: bg, color: '#fff', border: 'none', padding: '4px 10px',
  borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
});

export default RecruitmentOnboarding;
